import * as types from '../constants/actionTypes';

const initialState = {
  bills: [],
  currentBill: null,
  pendingChallans: [],
  poSummary: null,
  poReconciliation: null,
  statistics: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
  },
  pendingChallanPagination: {
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0,
  },
  loading: false,
  reconciliationLoading: false,
  actionLoading: false, // for verify / reject / close ops
  error: null,
  filters: {
    page: 1,
    limit: 10,
    bill_status: '',
    reconciliation_status: '',
    purchase_order_id: '',
    delivery_challan_id: '',
  },
};

// Helper: replace a bill in the list by id, and also update currentBill if it matches
const replaceBillInList = (state, updatedBill) => ({
  ...state,
  actionLoading: false,
  error: null,
  bills: state.bills.map((b) =>
    updatedBill && b.id === updatedBill.id ? updatedBill : b
  ),
  currentBill:
    state.currentBill?.id === updatedBill?.id
      ? updatedBill
      : state.currentBill,
});

const billReceiptReducer = (state = initialState, action) => {
  switch (action.type) {
    // ── Fetch Bills List ──────────────────────────────────────────────────────
    case types.FETCH_BILLS_REQUEST:
      return { ...state, loading: true }; // Don't clear error here - preserve verification errors

    case types.FETCH_BILLS_SUCCESS:
      return {
        ...state,
        loading: false,
        bills: action.payload.bills || [],
        pagination: action.payload.pagination || state.pagination,
        // Don't clear error here - preserve verification errors
      };

    case types.FETCH_BILLS_FAILURE:
      return { ...state, loading: false, error: action.payload };

    // ── Fetch Single Bill ─────────────────────────────────────────────────────
    case types.FETCH_BILL_REQUEST:
      return { ...state, loading: true, error: null };

    case types.FETCH_BILL_SUCCESS:
      return { ...state, loading: false, currentBill: action.payload, error: null };

    case types.FETCH_BILL_FAILURE:
      return { ...state, loading: false, error: action.payload };

    case types.CLEAR_CURRENT_BILL:
      return { ...state, currentBill: null };

    // ── Receive Bill ──────────────────────────────────────────────────────────
    case types.RECEIVE_BILL_REQUEST:
      return { ...state, actionLoading: true, error: null };

    case types.RECEIVE_BILL_SUCCESS:
      return {
        ...state,
        actionLoading: false,
        bills: [action.payload.data, ...state.bills].filter(Boolean),
        error: null,
      };

    case types.RECEIVE_BILL_FAILURE:
      return { ...state, actionLoading: false, error: action.payload };

    // ── Verify Bill ───────────────────────────────────────────────────────────
    case types.VERIFY_BILL_REQUEST:
      return { ...state, actionLoading: true, error: null }; // Clear previous errors when starting new verification

    case types.VERIFY_BILL_SUCCESS:
      return replaceBillInList(state, action.payload.data);

    case types.VERIFY_BILL_FAILURE:
      return { ...state, actionLoading: false, error: action.payload };

    // ── Reject Bill ───────────────────────────────────────────────────────────
    case types.REJECT_BILL_REQUEST:
      return { ...state, actionLoading: true, error: null };

    case types.REJECT_BILL_SUCCESS:
      return replaceBillInList(state, action.payload.data);

    case types.REJECT_BILL_FAILURE:
      return { ...state, actionLoading: false, error: action.payload };

    // ── Close Bill ────────────────────────────────────────────────────────────
    case types.CLOSE_BILL_REQUEST:
      return { ...state, actionLoading: true, error: null };

    case types.CLOSE_BILL_SUCCESS:
      return replaceBillInList(state, action.payload.data);

    case types.CLOSE_BILL_FAILURE:
      return { ...state, actionLoading: false, error: action.payload };

    // ── Pending Challans ──────────────────────────────────────────────────────
    case types.FETCH_PENDING_BILL_CHALLANS_REQUEST:
      return { ...state, loading: true, error: null };

    case types.FETCH_PENDING_BILL_CHALLANS_SUCCESS:
      return {
        ...state,
        loading: false,
        pendingChallans: action.payload.challans || [],
        pendingChallanPagination:
          action.payload.pagination || state.pendingChallanPagination,
        error: null,
      };

    case types.FETCH_PENDING_BILL_CHALLANS_FAILURE:
      return { ...state, loading: false, error: action.payload };

    // ── PO Bill Summary ───────────────────────────────────────────────────────
    case types.FETCH_PO_BILL_SUMMARY_REQUEST:
      return { ...state, loading: true, error: null };

    case types.FETCH_PO_BILL_SUMMARY_SUCCESS:
      return { ...state, loading: false, poSummary: action.payload, error: null };

    case types.FETCH_PO_BILL_SUMMARY_FAILURE:
      return { ...state, loading: false, error: action.payload };

    // ── PO 3-Way Reconciliation ───────────────────────────────────────────────
    case types.FETCH_PO_RECONCILIATION_REQUEST:
      return { ...state, reconciliationLoading: true, error: null };

    case types.FETCH_PO_RECONCILIATION_SUCCESS:
      return {
        ...state,
        reconciliationLoading: false,
        poReconciliation: action.payload,
        error: null,
      };

    case types.FETCH_PO_RECONCILIATION_FAILURE:
      return { ...state, reconciliationLoading: false, error: action.payload };

    // ── Bill Statistics ───────────────────────────────────────────────────────
    case types.FETCH_BILL_STATISTICS_REQUEST:
      return { ...state, loading: true, error: null };

    case types.FETCH_BILL_STATISTICS_SUCCESS:
      return { ...state, loading: false, statistics: action.payload, error: null };

    case types.FETCH_BILL_STATISTICS_FAILURE:
      return { ...state, loading: false, error: action.payload };

    // ── Filters ───────────────────────────────────────────────────────────────
    case types.SET_BILL_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case types.CLEAR_BILL_FILTERS:
      return { ...state, filters: initialState.filters };

    case types.CLEAR_BILL_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};

export default billReceiptReducer;
